import { apiClient, setAuthHeader } from '../config/api';

// Helper to normalize image paths - ensures /uploads/ paths go through the API server
// In production, images are served from v2api.stampoutsuicide.org, not the main domain
export const normalizeImagePath = (path: string | undefined): string | undefined => {
  if (!path) return undefined;

  // Get the API base URL (without /api suffix)
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

  // If path starts with /uploads/, convert to absolute URL pointing to API server
  if (path.startsWith('/uploads/')) {
    return `${apiBaseUrl}/api${path}`;
  }
  // If path starts with /api/uploads/, also make it absolute
  if (path.startsWith('/api/uploads/')) {
    return `${apiBaseUrl}${path}`;
  }
  return path;
};

export interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  postType: 'story' | 'news';
  content: string;
  excerpt: string;
  featuredImage?: {
    filename: string;
    originalName: string;
    path: string;
    alt?: string;
    caption?: string;
  };
  featured: boolean;
  externalUrl?: string;
  sourceName?: string;
  author: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  status: 'draft' | 'published';
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BlogPostFormData {
  title: string;
  content: string;
  postType: 'story' | 'news';
  featured?: boolean;
  externalUrl?: string;
  sourceName?: string;
  excerpt?: string;
  featuredImage?: {
    filename: string;
    originalName: string;
    path: string;
    alt?: string;
    caption?: string;
  };
}

interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface SingleResponse<T> {
  success: boolean;
  data: T;
}

// Public API Functions

export const fetchPublishedStories = async (page = 1, limit = 10): Promise<PaginatedResponse<BlogPost>> => {
  const response = await apiClient.get('/public/stories', { params: { page, limit } });
  return response.data as PaginatedResponse<BlogPost>;
};

export const fetchFeaturedStory = async (): Promise<BlogPost | null> => {
  try {
    const response = await apiClient.get('/public/stories/featured');
    return (response.data as SingleResponse<BlogPost>).data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const fetchStoryBySlug = async (slug: string): Promise<BlogPost> => {
  const response = await apiClient.get(`/public/stories/${slug}`);
  return (response.data as SingleResponse<BlogPost>).data;
};

export const fetchPublishedNews = async (page = 1, limit = 10): Promise<PaginatedResponse<BlogPost>> => {
  const response = await apiClient.get('/public/news', { params: { page, limit } });
  return response.data as PaginatedResponse<BlogPost>;
};

export const fetchFeaturedNews = async (): Promise<BlogPost | null> => {
  try {
    const response = await apiClient.get('/public/news/featured');
    return (response.data as SingleResponse<BlogPost>).data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const fetchNewsBySlug = async (slug: string): Promise<BlogPost> => {
  const response = await apiClient.get(`/public/news/${slug}`);
  return (response.data as SingleResponse<BlogPost>).data;
};

// Admin API Functions

export const fetchAdminPosts = async (
  page = 1,
  limit = 20,
  postType?: 'story' | 'news',
  status?: 'draft' | 'published'
): Promise<PaginatedResponse<BlogPost>> => {
  setAuthHeader(apiClient);
  const params: Record<string, any> = { page, limit };
  if (postType) params.postType = postType;
  if (status) params.status = status;
  const response = await apiClient.get('/admin/posts', { params });
  return response.data as PaginatedResponse<BlogPost>;
};

export const fetchPostById = async (id: string): Promise<BlogPost> => {
  setAuthHeader(apiClient);
  const response = await apiClient.get(`/admin/posts/${id}`);
  return (response.data as SingleResponse<BlogPost>).data;
};

export const createPost = async (data: BlogPostFormData): Promise<BlogPost> => {
  setAuthHeader(apiClient);
  const response = await apiClient.post('/admin/posts', data);
  return (response.data as SingleResponse<BlogPost>).data;
};

export const updatePost = async (id: string, data: Partial<BlogPostFormData>): Promise<BlogPost> => {
  setAuthHeader(apiClient);
  const response = await apiClient.put(`/admin/posts/${id}`, data);
  return (response.data as SingleResponse<BlogPost>).data;
};

export const deletePost = async (id: string): Promise<void> => {
  setAuthHeader(apiClient);
  await apiClient.delete(`/admin/posts/${id}`);
};

export const publishPost = async (id: string): Promise<BlogPost> => {
  setAuthHeader(apiClient);
  const response = await apiClient.post(`/admin/posts/${id}/publish`);
  return (response.data as SingleResponse<BlogPost>).data;
};

export const unpublishPost = async (id: string): Promise<BlogPost> => {
  setAuthHeader(apiClient);
  const response = await apiClient.post(`/admin/posts/${id}/unpublish`);
  return (response.data as SingleResponse<BlogPost>).data;
};

export const uploadImage = async (file: File): Promise<{ filename: string; originalName: string; path: string }> => {
  setAuthHeader(apiClient);
  const formData = new FormData();
  formData.append('image', file);
  const response = await apiClient.post('/admin/posts/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return (response.data as SingleResponse<{ filename: string; originalName: string; path: string }>).data;
};

export const updateFeaturedImage = async (
  id: string,
  file: File,
  alt?: string,
  caption?: string
): Promise<BlogPost> => {
  setAuthHeader(apiClient);
  const formData = new FormData();
  formData.append('image', file);
  if (alt) formData.append('alt', alt);
  if (caption) formData.append('caption', caption);
  const response = await apiClient.post(`/admin/posts/${id}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return (response.data as SingleResponse<BlogPost>).data;
};

export const deleteFeaturedImage = async (id: string): Promise<BlogPost> => {
  setAuthHeader(apiClient);
  const response = await apiClient.delete(`/admin/posts/${id}/image`);
  return (response.data as SingleResponse<BlogPost>).data;
};

// localStorage Draft Management

const STORAGE_KEY = 'notestream_post_draft';

export interface BlogDraft {
  postId: string | 'new';
  timestamp: number;
  data: {
    postType: 'story' | 'news';
    title: string;
    content: string;
    featured: boolean;
    externalUrl?: string;
    sourceName?: string;
  };
}

export const saveDraftToLocalStorage = (draft: BlogDraft): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
};

export const getDraftFromLocalStorage = (): BlogDraft | null => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const clearDraftFromLocalStorage = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export const blogService = {
  // Public
  fetchPublishedStories,
  fetchFeaturedStory,
  fetchStoryBySlug,
  fetchPublishedNews,
  fetchFeaturedNews,
  fetchNewsBySlug,
  // Admin
  fetchAdminPosts,
  fetchPostById,
  createPost,
  updatePost,
  deletePost,
  publishPost,
  unpublishPost,
  uploadImage,
  updateFeaturedImage,
  deleteFeaturedImage,
  // localStorage
  saveDraftToLocalStorage,
  getDraftFromLocalStorage,
  clearDraftFromLocalStorage
};

export default blogService;
